import {
  SearchResponse,
  ProcessingApiResponse,
  BatchProcessingRequest,
  BatchProcessingResponse,
  ProjectStatusResponse,
  ProcessingOverviewResponse,
  BatchProcessingConfiguration,
  BatchProcessingPerformance
} from '../types/asset';

// Log the API base URL when the service is initialized
// In production, use the same hostname as the current page
// In development, use the environment variable or fallback to localhost
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Always use relative path when served through nginx (Docker)
  // This will be proxied to the search-api service
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();
console.log('AssetService initialized with API_BASE_URL:', API_BASE_URL);
console.log('Environment variables:', {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV
});

export const assetService = {
  // Process all assets in a project
  async processProject(projectId: string): Promise<ProcessingApiResponse> {
    try {
      console.log('[AssetService] Starting processProject with projectId:', projectId);
      console.log('[AssetService] Current API_BASE_URL:', API_BASE_URL);

      const url = `${API_BASE_URL}/assets/processing/projects/${projectId}/process`;
      console.log('[AssetService] Full request URL:', url);

      const requestBody = { project_id: projectId };
      console.log('[AssetService] Request body:', requestBody);

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      };
      console.log('[AssetService] Request options:', requestOptions);

      console.log('[AssetService] Making fetch request...');
      const response = await fetch(url, requestOptions);
      console.log('[AssetService] Fetch response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If response is not JSON (like HTML error page), use status text
          errorData = { message: response.statusText || 'Unknown error' };
        }
        console.error('[AssetService] Error response:', errorData);
        throw new Error(errorData.message || 'Failed to process project');
      }

      let data;
      try {
        data = await response.json();
        console.log('[AssetService] Success response data:', data);
        return data;
      } catch (e) {
        console.error('[AssetService] Error parsing JSON response:', e);
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      console.error('[AssetService] Error in processProject:', error);
      throw error;
    }
  },

  // Search assets
  async searchAssets(query: string, offset: number = 0, limit: number = 10): Promise<SearchResponse> {
    const response = await fetch(
      `${API_BASE_URL}/assets/search?query=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to search assets');
    }

    return response.json();
  },

  // Get processing status for a project
  async getProcessingStatus(projectId: string): Promise<ProcessingApiResponse> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/projects/${projectId}/status`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get processing status');
    }

    return response.json();
  },

  // Stop processing for a project
  async stopProcessing(projectId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/projects/${projectId}/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to stop processing');
    }
  },

  // Delete asset
  async deleteAsset(assetId: string): Promise<{ message: string }> {
    console.log('[AssetService] Deleting asset:', assetId);
    try {
      const response = await fetch(`${API_BASE_URL}/assets/${assetId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to delete asset');
      }

      return await response.json();
    } catch (error) {
      console.error('[AssetService] Error deleting asset:', error);
      throw error;
    }
  },

  // Get individual asset status
  async getAssetStatus(assetId: string): Promise<ProcessingApiResponse> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/${assetId}/status`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get asset status');
    }

    return response.json();
  },

  // Enhanced Batch Processing Methods
  async startEnhancedBatchProcessing(projectId: string, request: BatchProcessingRequest): Promise<BatchProcessingResponse> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/projects/${projectId}/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to start enhanced batch processing');
    }

    return response.json();
  },

  async getEnhancedBatchProcessingStatus(projectId: string): Promise<ProjectStatusResponse> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/projects/${projectId}/status`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get enhanced batch processing status');
    }

    return response.json();
  },

  async cancelEnhancedBatchProcessing(projectId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/projects/${projectId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to cancel enhanced batch processing');
    }
  },

  async getBatchProcessingOverview(): Promise<ProcessingOverviewResponse> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/overview`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get batch processing overview');
    }

    return response.json();
  },

  async getBatchProcessingConfiguration(): Promise<BatchProcessingConfiguration> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/configuration`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get batch processing configuration');
    }

    return response.json();
  },

  async getBatchProcessingPerformance(): Promise<BatchProcessingPerformance> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/performance`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get batch processing performance');
    }

    return response.json();
  },

  async getPriorityQueueStatus(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/queue/status`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get priority queue status');
    }

    return response.json();
  },

  async getProcessingOverview(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/overview`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get processing overview');
    }

    return response.json();
  },

  async getPriorityLevels(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/priority/levels`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get priority levels');
    }

    return response.json();
  },

  // Process project with mixed asset profiles
  async processProjectWithProfiles(projectId: string, request: any): Promise<ProcessingApiResponse> {
    try {
      console.log('[AssetService] Starting processProjectWithProfiles with projectId:', projectId);
      console.log('[AssetService] Request with profiles:', request);

      const url = `${API_BASE_URL}/assets/processing/enhanced/projects/${projectId}/process-with-profiles`;
      console.log('[AssetService] Full request URL:', url);

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request)
      };
      console.log('[AssetService] Request options:', requestOptions);

      console.log('[AssetService] Making fetch request...');
      const response = await fetch(url, requestOptions);
      console.log('[AssetService] Fetch response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If response is not JSON (like HTML error page), use status text
          errorData = { message: response.statusText || 'Unknown error' };
        }
        console.error('[AssetService] Error response:', errorData);
        throw new Error(errorData.message || 'Failed to process project with profiles');
      }

      let data;
      try {
        data = await response.json();
        console.log('[AssetService] Success response data:', data);
        return data;
      } catch (e) {
        console.error('[AssetService] Error parsing JSON response:', e);
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      console.error('[AssetService] Error in processProjectWithProfiles:', error);
      throw error;
    }
  }
};

// Export individual functions for compatibility with EnhancedAssetProcessor
export const processProjectAssets = async (projectId: string, request: any): Promise<any> => {
  return assetService.startEnhancedBatchProcessing(projectId, request);
};

export const processProjectWithProfiles = async (projectId: string, request: any): Promise<any> => {
  return assetService.processProjectWithProfiles(projectId, request);
};

export const getProjectStatus = async (projectId: string): Promise<any> => {
  return assetService.getEnhancedBatchProcessingStatus(projectId);
};

export const cancelProjectProcessing = async (projectId: string): Promise<void> => {
  return assetService.cancelEnhancedBatchProcessing(projectId);
};

export const getPriorityQueueStatus = async (): Promise<any> => {
  return assetService.getPriorityQueueStatus();
};

export const getProcessingOverview = async (): Promise<any> => {
  return assetService.getProcessingOverview();
};

export const getPriorityLevels = async (): Promise<any> => {
  return assetService.getPriorityLevels();
}; 